import { useCallback, useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import SmartImage from "./SmartImage";

/**
 * Premium gallery + lightbox.
 *  - main image with thumbnail strip
 *  - click main image to open immersive fullscreen viewer
 *  - keyboard: ← → Esc
 *  - touch: swipe left/right
 *  - dark-luxury palette, smooth fade transitions
 */
export default function PropertyGallery({ images = [], propertyName = "", badge }) {
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);

  const safeImages = images?.length ? images : [""];
  const next = useCallback(() => setActive((i) => (i + 1) % safeImages.length), [safeImages.length]);
  const prev = useCallback(() => setActive((i) => (i - 1 + safeImages.length) % safeImages.length), [safeImages.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, next, prev]);

  // Swipe on the fullscreen image
  const touch = useRef({ x: 0 });
  const onTouchStart = (e) => { touch.current.x = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touch.current.x;
    if (dx > 50) prev();
    else if (dx < -50) next();
  };

  return (
    <>
      <div className="aspect-[16/10] overflow-hidden mb-4 relative group cursor-zoom-in" onClick={() => setOpen(true)} data-testid="gallery-open-btn">
        <SmartImage
          src={safeImages[active]}
          alt={`${propertyName} — photo ${active + 1}`}
          containerClassName="w-full h-full"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
        {badge && (
          <span data-testid="property-badge" className="absolute top-5 left-5 px-4 py-2 text-[0.7rem] tracking-[0.22em] uppercase font-semibold bg-gold text-black z-10">
            {badge}
          </span>
        )}
        <div className="absolute bottom-5 right-5 px-3 py-2 bg-black/55 backdrop-blur-md border border-white/15 text-white text-[0.65rem] tracking-[0.22em] uppercase flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <Maximize2 size={12} /> View gallery
        </div>
        {safeImages.length > 1 && (
          <span className="absolute bottom-5 left-5 px-2.5 py-1 bg-black/55 backdrop-blur-md border border-white/15 text-white/80 text-[0.62rem] tracking-[0.2em]">
            {active + 1} / {safeImages.length}
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {safeImages.map((src, i) => (
          <button
            key={i}
            type="button"
            data-testid={`gallery-thumb-${i}`}
            onClick={() => setActive(i)}
            className={`aspect-[4/3] overflow-hidden border-2 transition-all duration-300 ${active === i ? "border-gold" : "border-transparent opacity-55 hover:opacity-100"}`}
          >
            <SmartImage src={src} alt="" containerClassName="w-full h-full" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col animate-fadeIn"
          data-testid="lightbox"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
            <div className="text-white/80 font-serif text-base sm:text-lg truncate pr-4">{propertyName}</div>
            <div className="flex items-center gap-5">
              <span className="text-gold text-[0.72rem] tracking-[0.22em]" data-testid="lightbox-counter">
                {String(active + 1).padStart(2, "0")} / {String(safeImages.length).padStart(2, "0")}
              </span>
              <button type="button" data-testid="lightbox-close" onClick={() => setOpen(false)} className="text-white/70 hover:text-gold transition-colors">
                <X size={22} />
              </button>
            </div>
          </div>

          <div className="flex-1 relative flex items-center justify-center px-4 sm:px-12 py-6">
            <button
              type="button"
              data-testid="lightbox-prev"
              onClick={prev}
              className="hidden sm:flex absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/15 bg-black/40 hover:border-gold hover:text-gold text-white/80 items-center justify-center transition-all"
              aria-label="Previous photo"
            >
              <ChevronLeft size={22} />
            </button>
            <div className="w-full h-full max-h-[78vh] flex items-center justify-center">
              <SmartImage
                src={safeImages[active]}
                alt={`${propertyName} — photo ${active + 1}`}
                containerClassName="w-full h-full"
                className="w-full h-full object-contain"
              />
            </div>
            <button
              type="button"
              data-testid="lightbox-next"
              onClick={next}
              className="hidden sm:flex absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/15 bg-black/40 hover:border-gold hover:text-gold text-white/80 items-center justify-center transition-all"
              aria-label="Next photo"
            >
              <ChevronRight size={22} />
            </button>
          </div>

          {/* Thumbnail strip inside lightbox */}
          <div className="border-t border-white/5 px-4 py-3 overflow-x-auto">
            <div className="flex gap-2 mx-auto w-max">
              {safeImages.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`lightbox-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`w-16 h-12 sm:w-20 sm:h-14 flex-shrink-0 overflow-hidden border-2 transition-all ${active === i ? "border-gold opacity-100" : "border-transparent opacity-50 hover:opacity-100"}`}
                >
                  <SmartImage src={src} alt="" containerClassName="w-full h-full" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
